import { z } from "zod"

import {
  subscriptionBillingPeriods,
  subscriptionPlanIds,
  subscriptionStatuses,
} from "../../billing.js"
import { activeClientSignals } from "../../firm-billing.js"

export const ApiUsagePlanSchema = z.object({
  id: z.enum(subscriptionPlanIds),
  name: z.string(),
  period: z.enum(subscriptionBillingPeriods),
  /** Pages included for the billing period; null = unlimited (Enterprise). */
  pagesIncluded: z.number().int().nonnegative().nullable(),
})

export type ApiUsagePlan = z.infer<typeof ApiUsagePlanSchema>

/** One client that counts as active this billing month, and why. */
export const ApiActiveClientSchema = z.object({
  workspaceId: z.string().uuid(),
  name: z.string(),
  /** Every signal that made the client active this month. */
  reasons: z.array(z.enum(activeClientSignals)).min(1),
  /** When the first signal happened. */
  activeSince: z.string().datetime(),
})

export type ApiActiveClient = z.infer<typeof ApiActiveClientSchema>

/** Active clients this billing month, on a firm plan. */
export const ApiActiveClientsSummarySchema = z.object({
  count: z.number().int().nonnegative(),
  /** How many the plan covers; null when every active client is billed. */
  limit: z.number().int().positive().nullable(),
  /** Pages each active client adds to the shared pool; null on the free
   * tier, which has one fixed cap. */
  pagesPerClient: z.number().int().positive().nullable(),
  /** Price per active client per month in cents; 0 on the free tier. */
  pricePerClientCents: z.number().int().nonnegative(),
  clients: z.array(ApiActiveClientSchema),
})

export type ApiActiveClientsSummary = z.infer<
  typeof ApiActiveClientsSummarySchema
>

export const ApiUsageWorkspaceBreakdownSchema = z.object({
  workspaceId: z.string().uuid(),
  name: z.string(),
  pagesUsed: z.number().int().nonnegative(),
})

export type ApiUsageWorkspaceBreakdown = z.infer<
  typeof ApiUsageWorkspaceBreakdownSchema
>

export const ApiEntitlementsSchema = z.object({
  /** Whether new processing may be enqueued for this org. */
  canProcess: z.boolean(),
  /** Whether QuickBooks/accounting exports are permitted (paid + active only). */
  canExportAccounting: z.boolean(),
  /** Whether API keys may authenticate requests (active Pro and up, or trialing).
   * Defaulted so a client updated ahead of the API still parses. */
  canUseApi: z.boolean().default(false),
  /** Whether webhook endpoints may be managed and receive deliveries (active Pro and up, or trialing). */
  canUseWebhooks: z.boolean().default(false),
  /** Whether direct posting to accounting destinations is permitted (active Pro and up, or trialing). */
  canPostToDestinations: z.boolean().default(false),
  canUseAgents: z.boolean().default(false),
  /** Whether forwarded email is ingested for this org (active Pro and up, or trialing). */
  canUseEmailForwarding: z.boolean().default(false),
  /** Whether the hosted client portal accepts uploads. */
  canUsePortal: z.boolean().default(false),
  /** Whether usage has exceeded the included allowance (overage accruing). */
  isOverIncluded: z.boolean(),
  /** True when exceeding the allowance is refused rather than billed as
   * overage (trials). Drives the client-side pre-upload page check.
   * Defaulted so a client updated ahead of the API still parses. */
  hardPageCap: z.boolean().default(false),
  /** Whether this trial has been unlocked to the full allowance. Read it with
   * `status === "trialing"`: it is always false off a trial, where the concept
   * does not apply, and false plus trialing is what makes the offer appear. */
  trialUnlocked: z.boolean().default(false),
  /** Whether a locked trial may still overrun its allowance for one first
   * document. Lets the upload dialog promise the first document instead of
   * refusing a file it would in fact accept. */
  firstDocumentGrace: z.boolean().default(false),
  /** Why processing is blocked, or null when allowed. */
  blockReason: z
    .enum(["trial_expired", "quota_exceeded", "subscription_required"])
    .nullable(),
})

export type ApiEntitlements = z.infer<typeof ApiEntitlementsSchema>

/**
 * Read-only usage summary for an organization's current billing period. Pages
 * are metered from the usage ledger; pricing/overage charges are not applied
 * yet (PR 3 is the honest meter, enforcement + Stripe land later).
 */
export const ApiUsageSummaryResponseSchema = z.object({
  plan: ApiUsagePlanSchema,
  status: z.enum(subscriptionStatuses),
  /**
   * Whether the organization's subscription row is backed by a Stripe
   * subscription. Combined with `status`, this routes the web's plan buttons:
   * a live Stripe subscription (any non-canceled status, including past_due)
   * must change plan in place; checkout rejects it with 409. Optional so
   * older API deployments still parse.
   */
  hasStripeSubscription: z.boolean().optional(),
  trialEndsAt: z.string().datetime().nullable(),
  currentPeriodStart: z.string().datetime().nullable(),
  currentPeriodEnd: z.string().datetime().nullable(),
  pagesUsed: z.number().int().nonnegative(),
  /** null = unlimited allowance (Enterprise). */
  pagesIncluded: z.number().int().nonnegative().nullable(),
  /** Pages left before overage; null when the allowance is unlimited. */
  pagesRemaining: z.number().int().nullable(),
  /** Pages consumed beyond the included allowance (0 while within allowance). */
  overagePages: z.number().int().nonnegative(),
  /** Estimated overage cost this period, in cents (0 on trials / within allowance). */
  overageCents: z.number().int().nonnegative(),
  /** Per-page overage rate for the plan, in cents. null = no overage / unlimited. */
  overagePerPageCents: z.number().int().nonnegative().nullable(),
  /** Active team seats (members) used + included (null = unlimited). */
  seatsUsed: z.number().int().nonnegative(),
  seatsIncluded: z.number().int().nonnegative().nullable(),
  /** Active workspaces used + included (null = unlimited). */
  workspacesUsed: z.number().int().nonnegative(),
  workspacesIncluded: z.number().int().nonnegative().nullable(),
  entitlements: ApiEntitlementsSchema,
  workspaces: z.array(ApiUsageWorkspaceBreakdownSchema),
  /**
   * The family of the plan in force: "firm" is billed per active client.
   * Optional so older API deployments still parse; absent means "page".
   */
  planFamily: z.enum(["page", "firm"]).optional(),
  /** Which plans this organization may buy. A firm that pays for a page plan
   * is grandfathered and keeps "page". Absent means "page". */
  purchasablePlanFamily: z.enum(["page", "firm"]).optional(),
  /** Active clients this billing month. Null (or absent) off a firm plan. */
  activeClients: ApiActiveClientsSummarySchema.nullable().optional(),
})

export type ApiUsageSummaryResponse = z.infer<
  typeof ApiUsageSummaryResponseSchema
>
